import type { TenantSettings } from '@lastrouter/contracts';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, Input, Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from '@lastrouter/ui';
import { Plus, Trash2 } from '@lastrouter/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiQuery } from '../api/hooks.js';
import { EmptyState, QueryState } from '../components/common.js';
import { useSaveSettings } from './settings-save.js';

/*
 * Limites de uso (consultas por pessoa por minuto). A regra continua a mesma (settings-policy): exceção da
 * pessoa, depois a do produto, depois a geral do painel e por fim a do arquivo. A tela só se espalhou:
 * a geral fica em Configurações, a do produto na tela de cada produto e as exceções em Pessoas.
 */

export const validLimit = (s: string) => s === '' || (/^\d+$/.test(s) && Number(s) >= 1 && Number(s) <= 10_000);

function PerUserForm({ settings }: { settings: TenantSettings }) {
  const { t } = useTranslation();
  const { save, pending } = useSaveSettings('limits.saved');
  const toRows = (s: TenantSettings) => Object.entries(s.limits.per_user).map(([user, v]) => ({ user, limit: String(v) }));
  const [perUser, setPerUser] = useState(toRows(settings));
  useEffect(() => setPerUser(toRows(settings)), [settings]);
  const allValid = perUser.every((u) => u.user.trim() && validLimit(u.limit) && u.limit !== '');

  function submit() {
    save({ ...settings, limits: { ...settings.limits, per_user: Object.fromEntries(perUser.map((u) => [u.user.trim().toLowerCase(), Number(u.limit)])) } });
  }

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <CardTitle>{t('limits.perUser')}</CardTitle>
          <CardDescription>{t('limits.perUserHelp')}</CardDescription>
        </div>
        <Button size="sm" onClick={() => setPerUser((u) => [...u, { user: '', limit: '' }])}>
          <Plus />
          {t('limits.addUser')}
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {perUser.length === 0 ? (
          <EmptyState>{t('limits.noUsers')}</EmptyState>
        ) : (
          <Table>
            <TableCaption>{t('limits.perUser')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>{t('limits.user')}</TableHead>
                <TableHead>{t('limits.limit')}</TableHead>
                <TableHead>
                  <span className="sr-only">{t('common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {perUser.map((u, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Input aria-label={`${t('limits.user')} ${i + 1}`} type="email" value={u.user} onChange={(e) => setPerUser((p) => p.map((x, j) => (j === i ? { ...x, user: e.target.value } : x)))} />
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`${t('limits.limit')} ${i + 1}`}
                      aria-invalid={!validLimit(u.limit) || u.limit === '' ? true : undefined}
                      inputMode="numeric"
                      value={u.limit}
                      onChange={(e) => setPerUser((p) => p.map((x, j) => (j === i ? { ...x, limit: e.target.value.trim() } : x)))}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" aria-label={`${t('common.remove')} ${u.user || i + 1}`} onClick={() => setPerUser((p) => p.filter((_, j) => j !== i))}>
                      <Trash2 />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div>
          <Button variant="primary" disabled={!allValid || pending} onClick={submit}>
            {t('common.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/** Exceções de limite por pessoa (aba de Pessoas). */
export function PerUserLimits() {
  const q = useApiQuery('settings');
  return <QueryState query={q}>{(s) => <PerUserForm settings={s} />}</QueryState>;
}

function GlobalForm({ settings }: { settings: TenantSettings }) {
  const { t } = useTranslation();
  const { save, pending } = useSaveSettings('limits.saved');
  const [global, setGlobal] = useState(settings.limits.calls_per_minute?.toString() ?? '');
  useEffect(() => setGlobal(settings.limits.calls_per_minute?.toString() ?? ''), [settings]);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('limits.global')}</CardTitle>
        <CardDescription>{t('limits.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-3">
        <Field id="limite-geral" label={t('limits.limit')} hint={t('limits.globalHelp')} error={validLimit(global) ? undefined : t('limits.invalid')} className="max-w-xs">
          <Input inputMode="numeric" value={global} onChange={(e) => setGlobal(e.target.value.trim())} />
        </Field>
        <Button variant="primary" disabled={!validLimit(global) || pending} onClick={() => save({ ...settings, limits: { ...settings.limits, calls_per_minute: global ? Number(global) : null } })}>
          {t('common.save')}
        </Button>
      </CardContent>
    </Card>
  );
}

/** Limite geral padrão por pessoa (em Configurações). */
export function GlobalLimit() {
  const q = useApiQuery('settings');
  return <QueryState query={q}>{(s) => <GlobalForm settings={s} />}</QueryState>;
}
